/**
 * Astrography image viewer.
 *
 * A single near-fullscreen `<dialog>` that any gallery on the page can feed.
 * It is fully declarative — nothing imports it, nothing calls it:
 *
 *   [data-ag-lb-group]   a gallery. Optional `data-ag-lb-title` names it.
 *   [data-ag-lb-item]    one image inside that group, carrying
 *                        data-lb-src / data-lb-srcset  (the large source)
 *                        data-lb-alt / data-lb-caption / data-lb-thumb
 *   [data-ag-lb-open]    anything clickable that opens the group it sits in.
 *
 * An item that is itself a button (the product thumbnails are) opens at its
 * own index and gets clicked back on close, so the page keeps whatever image
 * the visitor stopped on.
 *
 * Viewing: click or tap the artwork to zoom, wheel or pinch for finer steps,
 * drag to pan, arrows/swipe to move between images, Esc to leave. The poster
 * detail is the product here, so `sizes` grows with the zoom level and the
 * browser fetches a sharper source as you go in.
 */

const MAX_SCALE = 4;
const TAP_SCALE = 2.5;
/** Shopify serves up to 5760px; past 3200 the wait costs more than the detail. */
const MAX_SOURCE_WIDTH = 3200;
/** Horizontal travel that counts as "next image" rather than a stray drag. */
const SWIPE_PX = 55;
/** Pointer travel below this is a tap, not a drag. */
const TAP_SLOP = 6;

/**
 * @typedef {object} LightboxItem
 * @property {string} src
 * @property {string} srcset
 * @property {string} alt
 * @property {string} caption
 * @property {string} thumb
 * @property {HTMLElement} node
 */

class AgLightbox extends HTMLElement {
  /** @type {AbortController} */
  #ac = new AbortController();
  /** @type {LightboxItem[]} */
  #items = [];
  #index = 0;
  #scale = 1;
  #tx = 0;
  #ty = 0;
  /** @type {Map<number, { x: number, y: number }>} */
  #pointers = new Map();
  /** @type {{ x: number, y: number, tx: number, ty: number, moved: boolean } | null} */
  #drag = null;
  #pinchDistance = 0;
  /** A drag that ends on the backdrop must not be read as a click that closes. */
  #swallowClick = false;
  #viewportWidth = 0;
  /** @type {HTMLElement | null} */
  #opener = null;
  /** @type {HTMLElement | null} */
  #group = null;

  connectedCallback() {
    const { signal } = this.#ac;
    const dialog = this.dialog;
    if (!dialog) return;

    document.addEventListener('click', this.#onDocumentClick, { signal });
    dialog.addEventListener('click', this.#onDialogClick, { signal });
    dialog.addEventListener('keydown', this.#onKeydown, { signal });
    // `close` covers Esc too — a `cancel` handler here would wipe the state
    // before `close` gets to hand the last-viewed image back to the page.
    dialog.addEventListener('close', this.#onClose, { signal });

    const stage = this.stage;
    stage?.addEventListener('wheel', this.#onWheel, { signal, passive: false });
    stage?.addEventListener('pointerdown', this.#onPointerDown, { signal });
    stage?.addEventListener('pointermove', this.#onPointerMove, { signal });
    stage?.addEventListener('pointerup', this.#onPointerUp, { signal });
    stage?.addEventListener('pointercancel', this.#onPointerUp, { signal });

    this.image?.addEventListener('load', () => this.classList.remove('is-loading'), { signal });
    window.addEventListener('resize', this.#onResize, { signal });
  }

  disconnectedCallback() {
    this.#ac.abort();
  }

  /** @returns {HTMLDialogElement | null} */
  get dialog() {
    return this.querySelector('dialog');
  }
  /** @returns {HTMLElement | null} */
  get stage() {
    return this.querySelector('[data-ag-lb-stage]');
  }
  /** @returns {HTMLImageElement | null} */
  get image() {
    const img = this.querySelector('[data-ag-lb-img]');
    return img instanceof HTMLImageElement ? img : null;
  }

  /* ---------- opening ---------- */

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    // One viewer serves the whole page even if a section renders a second one.
    if (document.querySelector('ag-lightbox') !== this) return;
    const trigger = target.closest('[data-ag-lb-open]');
    if (!(trigger instanceof HTMLElement)) return;
    const group = trigger.closest('[data-ag-lb-group]');
    if (!(group instanceof HTMLElement)) return;

    event.preventDefault();
    this.#group = group;
    this.#opener = trigger;
    const items = collect(group);
    if (!items.length) return;

    const self = trigger.closest('[data-ag-lb-item]');
    let index = self ? items.findIndex((item) => item.node === self) : -1;
    if (index < 0) index = items.findIndex((item) => item.node.classList.contains('is-active'));
    this.open(items, index < 0 ? 0 : index);
  };

  /**
   * @param {LightboxItem[]} items
   * @param {number} index
   */
  open(items, index = 0) {
    const dialog = this.dialog;
    if (!dialog || !items.length) return;
    this.#items = items;

    // Open first: the stage has no width until it is on screen, and `sizes`
    // is measured off it.
    if (!dialog.open) dialog.showModal();
    // `showModal` alone leaves iOS scrolling the page behind the dialog.
    document.documentElement.style.overflow = 'hidden';
    this.#viewportWidth = window.innerWidth;

    this.#buildStrip();
    this.#show(index, false);

    const title = this.querySelector('[data-ag-lb-title]');
    if (title instanceof HTMLElement) {
      title.textContent = this.#group?.dataset.agLbTitle || '';
    }

    this.querySelector('[data-ag-lb-close]')?.focus({ preventScroll: true });
  }

  #onClose = () => {
    document.documentElement.style.overflow = '';
    // Leave the page showing whatever the visitor stopped on.
    const node = this.#items[this.#index]?.node;
    if (node instanceof HTMLButtonElement && !node.classList.contains('is-active')) node.click();
    this.#opener?.focus({ preventScroll: true });
    this.#reset();
  };

  #reset() {
    this.#items = [];
    this.#pointers.clear();
    this.#drag = null;
    this.#setZoom(1, 0, 0);
    this.classList.remove('is-dragging', 'is-loading');
  }

  /* ---------- moving between images ---------- */

  /**
   * @param {number} index
   * @param {boolean} [wrap]
   */
  #show(index, wrap = true) {
    const total = this.#items.length;
    if (!total) return;
    this.#index = wrap ? (index + total) % total : Math.max(0, Math.min(total - 1, index));
    const item = this.#items[this.#index];
    const img = this.image;
    if (!img || !item) return;

    this.#setZoom(1, 0, 0);
    this.classList.add('is-loading');
    img.alt = item.alt;
    img.sizes = `${this.#stageWidth()}px`;
    img.srcset = item.srcset;
    img.src = item.src;
    if (img.complete) this.classList.remove('is-loading');

    this.#setText('[data-ag-lb-caption]', item.caption);
    this.#setText('[data-ag-lb-index]', String(this.#index + 1));
    this.#setText('[data-ag-lb-total]', String(total));

    this.querySelectorAll('[data-ag-lb-thumb]').forEach((el, i) => {
      if (i === this.#index) el.setAttribute('aria-current', 'true');
      else el.removeAttribute('aria-current');
      if (i === this.#index) el.scrollIntoView({ block: 'nearest', inline: 'nearest' });
    });

    const single = total < 2;
    this.querySelectorAll('[data-ag-lb-prev], [data-ag-lb-next]').forEach((el) => {
      if (el instanceof HTMLButtonElement) el.disabled = single;
    });

    this.#preload(this.#index + 1);
    this.#preload(this.#index - 1);
  }

  /** The widest the artwork can render unzoomed — the `sizes` starting point. */
  #stageWidth() {
    const width = this.stage?.clientWidth || window.innerWidth;
    return Math.round(Math.min(width, MAX_SOURCE_WIDTH));
  }

  /** @param {number} index */
  #preload(index) {
    const total = this.#items.length;
    if (total < 2) return;
    const item = this.#items[(index + total) % total];
    if (!item) return;
    const img = new Image();
    img.sizes = `${this.#stageWidth()}px`;
    img.srcset = item.srcset;
    img.src = item.src;
  }

  #buildStrip() {
    const strip = this.querySelector('[data-ag-lb-strip]');
    if (!(strip instanceof HTMLElement)) return;
    strip.hidden = this.#items.length < 2;
    strip.replaceChildren(
      ...this.#items.map((item, i) => {
        const button = document.createElement('button');
        button.type = 'button';
        button.className = 'ag-lb__thumb';
        button.dataset.agLbThumb = String(i);
        button.setAttribute('aria-label', item.caption || `Image ${i + 1}`);
        const img = document.createElement('img');
        img.src = item.thumb;
        img.alt = '';
        img.loading = 'lazy';
        button.append(img);
        return button;
      })
    );
  }

  /**
   * @param {string} selector
   * @param {string} text
   */
  #setText(selector, text) {
    const el = this.querySelector(selector);
    if (el instanceof HTMLElement) el.textContent = text;
  }

  /* ---------- chrome ---------- */

  /** @param {MouseEvent} event */
  #onDialogClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (this.#swallowClick) {
      this.#swallowClick = false;
      return;
    }

    if (target.closest('[data-ag-lb-close]')) return this.dialog?.close();
    if (target.closest('[data-ag-lb-prev]')) return this.#show(this.#index - 1);
    if (target.closest('[data-ag-lb-next]')) return this.#show(this.#index + 1);

    const thumb = target.closest('[data-ag-lb-thumb]');
    if (thumb instanceof HTMLElement) return this.#show(Number(thumb.dataset.agLbThumb || 0), false);

    const zoom = target.closest('[data-ag-lb-zoom]');
    if (zoom) return this.#toggleZoom();

    // Anything left inside the stage but outside the artwork closes the view.
    if (target.closest('[data-ag-lb-stage]') && target !== this.image) this.dialog?.close();
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    switch (event.key) {
      case 'ArrowRight':
        event.preventDefault();
        return this.#show(this.#index + 1);
      case 'ArrowLeft':
        event.preventDefault();
        return this.#show(this.#index - 1);
      case 'Home':
        event.preventDefault();
        return this.#show(0, false);
      case 'End':
        event.preventDefault();
        return this.#show(this.#items.length - 1, false);
      case '+':
      case '=':
        event.preventDefault();
        return this.#zoomAt(this.#centre(), this.#scale * 1.4);
      case '-':
        event.preventDefault();
        return this.#zoomAt(this.#centre(), this.#scale / 1.4);
      case '0':
        event.preventDefault();
        return this.#setZoom(1, 0, 0);
      case 'z':
      case 'Z':
        event.preventDefault();
        return this.#toggleZoom();
    }
  };

  // A mobile URL bar sliding away fires `resize` too; only a real width change
  // invalidates the pan bounds.
  #onResize = () => {
    if (!this.dialog?.open || window.innerWidth === this.#viewportWidth) return;
    this.#viewportWidth = window.innerWidth;
    this.#setZoom(1, 0, 0);
  };

  /* ---------- zoom and pan ---------- */

  #centre() {
    const rect = this.stage?.getBoundingClientRect();
    if (!rect) return { x: 0, y: 0 };
    return { x: rect.left + rect.width / 2, y: rect.top + rect.height / 2 };
  }

  #toggleZoom() {
    if (this.#scale > 1) this.#setZoom(1, 0, 0);
    else this.#zoomAt(this.#centre(), TAP_SCALE);
  }

  /**
   * Scales around a viewport point, so whatever is under the cursor stays put.
   * @param {{ x: number, y: number }} point
   * @param {number} target
   */
  #zoomAt(point, target) {
    const img = this.image;
    if (!img) return;
    const next = Math.max(1, Math.min(MAX_SCALE, target));
    if (next === this.#scale) return;
    if (next === 1) return this.#setZoom(1, 0, 0);

    const rect = img.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    const ux = (point.x - cx) / this.#scale;
    const uy = (point.y - cy) / this.#scale;
    this.#setZoom(next, this.#tx + (point.x - ux * next) - cx, this.#ty + (point.y - uy * next) - cy);
  }

  /**
   * @param {number} scale
   * @param {number} tx
   * @param {number} ty
   */
  #setZoom(scale, tx, ty) {
    const img = this.image;
    if (!img) return;
    this.#scale = scale;

    if (scale > 1) {
      const stage = this.stage?.getBoundingClientRect();
      const width = img.clientWidth * scale;
      const height = img.clientHeight * scale;
      const maxX = Math.max(0, (width - (stage?.width || 0)) / 2);
      const maxY = Math.max(0, (height - (stage?.height || 0)) / 2);
      this.#tx = Math.max(-maxX, Math.min(maxX, tx));
      this.#ty = Math.max(-maxY, Math.min(maxY, ty));
    } else {
      this.#tx = 0;
      this.#ty = 0;
    }

    img.style.transform =
      scale === 1 ? '' : `translate3d(${this.#tx}px, ${this.#ty}px, 0) scale(${scale})`;
    // Ask the browser for a source that matches what is actually on screen.
    const wanted = Math.round(Math.min(img.clientWidth * scale, MAX_SOURCE_WIDTH));
    if (wanted > 0) img.sizes = `${wanted}px`;

    this.classList.toggle('is-zoomed', scale > 1);
    const button = this.querySelector('[data-ag-lb-zoom]');
    button?.setAttribute('aria-pressed', scale > 1 ? 'true' : 'false');
  }

  /** @param {WheelEvent} event */
  #onWheel = (event) => {
    event.preventDefault();
    const factor = Math.exp(-event.deltaY / 320);
    this.#zoomAt({ x: event.clientX, y: event.clientY }, this.#scale * factor);
  };

  /** @param {PointerEvent} event */
  #onPointerDown = (event) => {
    if (event.button !== 0 && event.pointerType === 'mouse') return;
    // The prev/next buttons sit inside the stage; capturing their pointer here
    // would swallow their click.
    if (event.target instanceof Element && event.target.closest('button')) return;
    this.#swallowClick = false;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
    if (this.#pointers.size === 2) {
      this.#pinchDistance = this.#spread();
      this.#drag = null;
      return;
    }
    this.stage?.setPointerCapture(event.pointerId);
    this.#drag = { x: event.clientX, y: event.clientY, tx: this.#tx, ty: this.#ty, moved: false };
  };

  /** @param {PointerEvent} event */
  #onPointerMove = (event) => {
    if (!this.#pointers.has(event.pointerId)) return;
    this.#pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (this.#pointers.size === 2) {
      const spread = this.#spread();
      if (this.#pinchDistance > 0) {
        this.classList.add('is-dragging');
        this.#zoomAt(this.#midpoint(), this.#scale * (spread / this.#pinchDistance));
      }
      this.#pinchDistance = spread;
      return;
    }

    const drag = this.#drag;
    if (!drag) return;
    const dx = event.clientX - drag.x;
    const dy = event.clientY - drag.y;
    if (!drag.moved && Math.hypot(dx, dy) < TAP_SLOP) return;
    drag.moved = true;

    if (this.#scale > 1) {
      this.classList.add('is-dragging');
      this.#setZoom(this.#scale, drag.tx + dx, drag.ty + dy);
    } else if (event.pointerType !== 'mouse' && this.#items.length > 1) {
      // Unzoomed on touch, a horizontal drag follows the finger and then lands.
      this.classList.add('is-dragging');
      const img = this.image;
      if (img) img.style.transform = `translate3d(${dx * 0.5}px, 0, 0)`;
    }
  };

  /** @param {PointerEvent} event */
  #onPointerUp = (event) => {
    const start = this.#drag;
    this.#pointers.delete(event.pointerId);
    if (this.#pointers.size < 2) this.#pinchDistance = 0;
    if (this.stage?.hasPointerCapture(event.pointerId)) this.stage.releasePointerCapture(event.pointerId);
    this.classList.remove('is-dragging');
    this.#drag = null;
    if (!start) return;

    const dx = event.clientX - start.x;

    if (!start.moved) {
      // A clean tap on the artwork zooms; the dialog click handler covers the rest.
      if (event.target === this.image) {
        this.#swallowClick = true;
        this.#toggleZoom();
      }
      return;
    }

    // Anything that moved was a drag, not a click on the backdrop.
    this.#swallowClick = true;

    if (this.#scale === 1 && this.#items.length > 1) {
      const img = this.image;
      if (img) img.style.transform = '';
      if (Math.abs(dx) > SWIPE_PX) this.#show(this.#index + (dx < 0 ? 1 : -1));
    }
  };

  #spread() {
    const [a, b] = [...this.#pointers.values()];
    return a && b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
  }

  #midpoint() {
    const [a, b] = [...this.#pointers.values()];
    if (!a || !b) return this.#centre();
    return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
  }
}

/**
 * Reads a gallery's items straight off the DOM, so a section re-render (the
 * product page swaps its parts on every variant change) is picked up for free.
 * @param {HTMLElement} group
 * @returns {LightboxItem[]}
 */
function collect(group) {
  return [...group.querySelectorAll('[data-ag-lb-item]')]
    .filter((node) => node instanceof HTMLElement && node.dataset.lbSrc)
    .map((node) => {
      const el = /** @type {HTMLElement} */ (node);
      const inner = el.querySelector('img');
      return {
        src: el.dataset.lbSrc || '',
        srcset: el.dataset.lbSrcset || '',
        alt: el.dataset.lbAlt || '',
        caption: el.dataset.lbCaption || '',
        thumb: el.dataset.lbThumb || inner?.currentSrc || inner?.src || el.dataset.lbSrc || '',
        node: el,
      };
    });
}

if (!customElements.get('ag-lightbox')) customElements.define('ag-lightbox', AgLightbox);
